import { callGateway, type CallerIdentity } from "./gateway";
import { config } from "../config";

/**
 * The operator's short name: the address of its customer portal,
 * ankhos.io/o/<slug> (radius-wiki customer-portal-design.md §3.1, owner
 * 2026-10-19). Held by radaccess (v1.128) in radius.radaccess_operator_slug.
 *
 * 🔑 Same grant as the portal contact (api/contact.ts): the `operatorcontact`
 * resource, so an Operator may set its own. Rules the server enforces:
 *   - 3-30 characters, lower case letters, digits and single hyphens, starting
 *     with a letter and not ending with a hyphen;
 *   - a few words are reserved (api, admin, login ...);
 *   - at most one change every 30 days (409);
 *   - a slug another operator holds, now or before, is refused (409);
 *   - an old slug keeps redirecting to the new one for 2 years.
 */

const enc = (s: string) => encodeURIComponent(s);

export interface OperatorSlug {
  namespace: string;
  /** The ACTIVE short name, "" when none is set. */
  slug: string;
  /** When the ACTIVE short name was set (UTC ISO). */
  changedAt?: string;
  /** The earliest time a new short name is accepted; absent when a change is allowed now. */
  nextChangeAt?: string;
  /** Earlier short names that still redirect here. */
  retired: string[];
}

/** The public address a short name gives. */
export const PORTAL_BASE = "https://ankhos.io/o/";

const SHAPE = /^[a-z][a-z0-9-]{1,28}[a-z0-9]$/;

/** Mirrors radaccess app/handler/operatorslug.go reservedSlugs. The server stays the authority. */
export const RESERVED_SLUGS: ReadonlySet<string> = new Set([
  "api", "admin", "www", "portal", "account", "accounts", "ankh", "ankhos", "ankhnet", "login", "signin", "signup",
  "logout", "signout", "pay", "payment", "help", "support", "static", "assets", "site", "sites", "app", "console",
  "invite", "auth", "access", "status", "health", "test",
]);

/** What the field holds: trimmed and lower-cased, as an operator would mean it. */
export const normaliseSlug = (v: string) => v.trim().toLowerCase();

/** Why `slug` cannot be saved, or null. The same rules radaccess applies. */
export function slugProblem(slug: string): string | null {
  if (!slug) return "Enter a short name.";
  if (slug.length < 3 || slug.length > 30) return "3 to 30 characters.";
  if (!SHAPE.test(slug) || slug.includes("--")) {
    return "Lower case letters, digits and single hyphens; start with a letter, do not end with a hyphen.";
  }
  if (RESERVED_SLUGS.has(slug)) return `"${slug}" is reserved; choose another.`;
  return null;
}

export const getOperatorSlug = (i: CallerIdentity, ns: string) =>
  callGateway<OperatorSlug>(i, config().gatewayBase, `/operator/${enc(ns)}/slug`);

export const saveOperatorSlug = (i: CallerIdentity, ns: string, slug: string) =>
  callGateway<OperatorSlug>(i, config().gatewayBase, `/operator/${enc(ns)}/slug`, {
    method: "PUT",
    body: { slug: normaliseSlug(slug) },
  });
